import { useMemo } from 'react';
import { useAppContext } from 'src/components/app-context';
import type { ProductOfTheYearAward, IsProductOfTheYear } from './types';

/**
 * Retrive Product of the Year awards
 *
 * @param {number} [year] Year to retrive awards from. If omitted retrive all years
 * @returns Array of ProductOfTheYearAward, sorted by year
 */
export const useProductOfTheYearAwards = (
  year?: number
): ProductOfTheYearAward[] => {
  const { productOfTheYear } = useAppContext();

  return useMemo(
    (): ProductOfTheYearAward[] =>
      productOfTheYear!
        .filter((award): award is Exclude<typeof award, undefined | null> =>
          year ? year === award?.year : true
        )
        .map(award => ({
          awardName: award.awardName,
          awardLogo: award.awardLogo!,
          year: award.year,
          productId: award.product!.id,
        }))
        .sort((a, b) => a.year - b.year),
    [productOfTheYear, year]
  );
};

/**
 * Check if product has Product of the Year award
 *
 * @param {(string | number)} [productId]
 * @returns IsProductOfTheYear object. Latest year if several awards are present. If no award then false is returned.
 */
export const useIsProductOfTheYear = (
  productId?: string | number
): IsProductOfTheYear | false => {
  const productOfTheYearAwards = useProductOfTheYearAwards();

  if (!productId) {
    return false;
  }

  const thisProductOfTheYearAwards = productOfTheYearAwards.filter(
    award => `${productId}` === `${award.productId}`
  );

  if (thisProductOfTheYearAwards.length === 0) {
    return false;
  }

  const latestAward = thisProductOfTheYearAwards.pop();

  return latestAward || false;
};
